import { useEffect, useMemo, useRef, useState } from 'react';

import { Iframe } from '@/components/ui/Iframe';
import { Skeleton } from '@/components/ui/skeleton';
import { openUntrustedUrl } from '@/utils/external-links';

import {
  OFFICE_FRAME_CHANNEL,
  parseOfficeFrameEvent,
  type OfficeFrameKind,
} from './frame/office-frame-protocol';
import { loadOfficeFrameSrcDoc } from './frame/office-frame-srcdoc';

import type { OfficePreviewProps } from './office-preview.types';

const OFFICE_FRAME_TITLES: Record<OfficeFrameKind, string> = {
  docx: 'Word document preview',
  pptx: 'Presentation preview',
};

const createOfficeFrameNonce = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');

interface OfficeFrameProps extends OfficePreviewProps {
  kind: OfficeFrameKind;
}

export function OfficeFrame({ kind, bytes, onError }: OfficeFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const nonce = useMemo(createOfficeFrameNonce, []);
  const [srcDoc, setSrcDoc] = useState<string>();
  const [isRendering, setIsRendering] = useState(true);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;

      const message = parseOfficeFrameEvent(event.data, nonce);
      if (!message) return;

      if (message.type === 'ready') {
        frameWindow.postMessage(
          { channel: OFFICE_FRAME_CHANNEL, nonce, type: 'render', bytes },
          '*'
        );
      } else if (message.type === 'rendered') {
        setIsRendering(false);
      } else if (message.type === 'error') {
        onError();
      } else {
        openUntrustedUrl(message.href);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [bytes, nonce, onError]);

  useEffect(() => {
    let isStale = false;
    void loadOfficeFrameSrcDoc(kind, nonce).then(
      (frameDocument) => {
        if (!isStale) setSrcDoc(frameDocument);
      },
      () => {
        if (!isStale) onError();
      }
    );
    return () => {
      isStale = true;
    };
  }, [kind, nonce, onError]);

  return (
    <div className="relative h-full w-full bg-surface">
      {srcDoc ? (
        <Iframe
          ref={iframeRef}
          title={OFFICE_FRAME_TITLES[kind]}
          srcDoc={srcDoc}
          sandbox="allow-scripts"
          className="h-full w-full border-0 bg-white"
        />
      ) : null}
      {isRendering ? (
        <Skeleton
          variant="low-emphasis"
          className="absolute inset-0"
          data-testid="office-frame-loading"
        />
      ) : null}
    </div>
  );
}
